import { useEffect } from "react";
import { DRAFT_CONFIG, VALIDATION_MESSAGES } from "../utils/constants";
import { validateDraftData } from "../utils/widgetForm/validation";
import { formatDate } from "../utils/sanitize";
import logger from "../utils/logger";

/**
 * Hook to manage widget form draft in localStorage
 * Handles save, restore, and clear operations
 */
const useWidgetFormDraft = (
  id,
  formData,
  setFormData,
  setIsFormDirty,
  toast,
) => {
  // Restore draft on mount (only for new widgets)
  useEffect(() => {
    if (id) return; // Skip for editing existing widgets

    try {
      const savedDraft = localStorage.getItem(DRAFT_CONFIG.STORAGE_KEY);
      if (savedDraft) {
        const parsedDraft = JSON.parse(savedDraft);

        // Check draft expiration
        if (parsedDraft._timestamp) {
          const draftAge = Date.now() - parsedDraft._timestamp;

          if (draftAge > DRAFT_CONFIG.MAX_AGE_MS) {
            logger.info("Draft expired, clearing localStorage");
            localStorage.removeItem(DRAFT_CONFIG.STORAGE_KEY);
            toast.info(VALIDATION_MESSAGES.DRAFT_EXPIRED);
            return;
          }
        }

        // Validate draft structure before using (modifies data in place for sanitization)
        if (validateDraftData(parsedDraft)) {
          // Save timestamp before removing it for the toast message
          const savedTimestamp = parsedDraft._timestamp || Date.now();

          // Remove timestamp before setting form data
          delete parsedDraft._timestamp;
          setFormData(parsedDraft);
          setIsFormDirty(true);

          // Notify user that draft was restored with the correct timestamp
          toast.info(
            `${VALIDATION_MESSAGES.DRAFT_RESTORED} ${formatDate(savedTimestamp)}`,
          );
        } else {
          logger.warn(
            "Invalid draft data structure detected, clearing localStorage",
          );
          localStorage.removeItem(DRAFT_CONFIG.STORAGE_KEY);
          toast.warning("Saved draft was invalid and has been cleared.");
        }
      }
    } catch (err) {
      // Clear corrupted localStorage data
      logger.error("Failed to restore form draft", { error: err.message });
      try {
        localStorage.removeItem(DRAFT_CONFIG.STORAGE_KEY);
        toast.warning("Could not restore draft. Starting fresh.");
      } catch (clearErr) {
        logger.error("Failed to clear corrupted draft", {
          error: clearErr.message,
        });
      }
    }
    // INTENTIONAL: toast, setFormData, and setIsFormDirty are stable functions from hooks/parent.
    // We only want this effect to re-run when the widget id changes (create vs edit mode).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Save draft on formData change (only for new widgets)
  useEffect(() => {
    if (!id && formData.name) {
      try {
        const draftData = {
          ...formData,
          _timestamp: Date.now(), // Add timestamp for expiration check
        };
        localStorage.setItem(
          DRAFT_CONFIG.STORAGE_KEY,
          JSON.stringify(draftData),
        );
      } catch (err) {
        // Log storage errors (likely quota exceeded)
        logger.error("Failed to save form draft", {
          error: err.message,
          errorName: err.name,
        });
        // Attempt to clear old draft to make room
        if (err.name === "QuotaExceededError") {
          try {
            localStorage.removeItem(DRAFT_CONFIG.STORAGE_KEY);
          } catch (clearErr) {
            logger.error("Failed to clear draft after quota error", {
              error: clearErr.message,
            });
          }
        }
      }
    }
  }, [id, formData]);

  const clearDraft = () => {
    localStorage.removeItem(DRAFT_CONFIG.STORAGE_KEY);
  };

  return { clearDraft };
};

export default useWidgetFormDraft;
